import { useState } from "react";
import { standingHardBalanceFailures } from "@capture/shared";
import {
  type BalanceParkedBatch,
  patchHeaderValues,
  overrideBalanceFailure,
  clearBalanceReview,
} from "../api/client";
import styles from "../pages/ClassifyQueuePage.module.css";

/** R-264 5e-2 — declared-vs-actual table + actions for a balance_review batch.
 *  Edit-declared calls the Slice-1 PATCH (re-runs the gate → immediate unpark
 *  on pass); Override is soft-only (the API 409s hard rules); Clear is the I-3
 *  manual door (soft-only, audited). Parent refreshes via onChanged. */
export function BatchBalancePanel({
  batch,
  onChanged,
}: {
  batch: BalanceParkedBatch;
  onChanged: () => void;
}) {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const standingHard = standingHardBalanceFailures(batch.failures);

  // F-025 — declared comparands the operator can SEED even when no header value
  // exists yet (Phase 4.3 header config deferred): the field each failing rule
  // compares against (declaredFieldRef) that isn't already a header key.
  const existingKeys = Object.keys(batch.headerFieldValues);
  const seedableKeys = [
    ...new Set(
      batch.failures
        .map((f) => f.declaredFieldRef)
        .filter((k): k is string => !!k && !existingKeys.includes(k)),
    ),
  ];
  // All keys the declared-values editor offers: existing (edit) + seedable (new).
  const declaredKeys = [...existingKeys, ...seedableKeys];

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  function delta(f: { declared: unknown; actual: unknown }): string | null {
    const d = Number(f.declared);
    const a = Number(f.actual);
    return Number.isFinite(d) && Number.isFinite(a) ? String(a - d) : null;
  }

  function submitEdits() {
    const values: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(edits)) {
      if (v === "") continue;
      const n = Number(v);
      values[k] = Number.isFinite(n) && v.trim() !== "" ? n : v;
    }
    if (Object.keys(values).length === 0) return;
    void run(() => patchHeaderValues(batch.batchId, values));
  }

  return (
    <div className={styles.balancePanel} data-testid="batch-balance-panel">
      <table className={styles.balanceTable}>
        <thead>
          <tr>
            <th>Rule</th>
            <th>Declared</th>
            <th>Actual</th>
            <th>Δ</th>
            <th>Severity</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {batch.failures.map((f) => (
            <tr key={f.ruleId}>
              <td>{f.ruleName}</td>
              <td>{String(f.declared ?? "—")}</td>
              <td>{String(f.actual ?? "—")}</td>
              <td>{delta(f) ?? "—"}</td>
              <td>{f.overrideBehavior === "block" ? "hard" : "soft"}</td>
              <td>
                {f.overridden ? (
                  <span className={styles.overriddenTag}>Overridden</span>
                ) : f.overrideBehavior !== "block" ? (
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    disabled={busy}
                    aria-label={`Override ${f.ruleName}`}
                    onClick={() =>
                      void run(() => overrideBalanceFailure(batch.batchId, f.ruleId))
                    }
                  >
                    Override
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {declaredKeys.length > 0 && (
        <div className={styles.balanceEdits}>
          <span className={styles.balanceEditsTitle}>Declared header values</span>
          {declaredKeys.map((key) => {
            const existing = (batch.headerFieldValues as Record<string, unknown>)[key];
            const isSeed = !existingKeys.includes(key);
            return (
              <label key={key} className={styles.balanceEditRow}>
                {key}{isSeed ? ' (not yet declared)' : ''}
                <input
                  value={edits[key] ?? (existing !== undefined ? String(existing) : '')}
                  onChange={(e) =>
                    setEdits((p) => ({ ...p, [key]: e.target.value }))
                  }
                  disabled={busy}
                  placeholder={isSeed ? 'Enter a declared value' : undefined}
                  aria-label={`Declared value for ${key}`}
                />
              </label>
            );
          })}
          <button
            type="button"
            className={styles.btn}
            disabled={busy}
            onClick={submitEdits}
          >
            Save declared values
          </button>
        </div>
      )}

      {standingHard.length === 0 && (
        <button
          type="button"
          className={styles.btnSecondary}
          disabled={busy}
          onClick={() => void run(() => clearBalanceReview(batch.batchId))}
        >
          Clear balance review
        </button>
      )}

      {error && <p className={styles.rowError}>{error}</p>}
    </div>
  );
}
